import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getMemberById } from '../../../services/members';
import { formatCurrency, formatDate } from '../../../utils/formatters';
import LoadingSpinner from '../../../components/Common/LoadingSpinner';
import { 
  ArrowLeft, Mail, Phone, MapPin, 
  User, ShieldCheck, AlertCircle, 
  Users, CreditCard, ExternalLink
} from 'lucide-react';

const MemberDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [member, setMember] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadMember();
  }, [id]);

  const loadMember = async () => {
    try {
      setLoading(true);
      const data = await getMemberById(id);
      setMember(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="h-96 flex items-center justify-center"><LoadingSpinner /></div>;
  if (!member) return (
    <div className="p-10 text-center">
      <div className="text-slate-400 mb-4"><AlertCircle size={48} className="mx-auto" /></div>
      <h2 className="text-xl font-bold text-slate-900">Member Not Found</h2>
      <button onClick={() => navigate(-1)} className="mt-4 text-indigo-600 font-bold">Return to Registry</button>
    </div>
  );

  const isHighRisk = member.missed_pay_count > 0;

  return (
    <div className="p-6 md:p-10 max-w-6xl mx-auto space-y-8 animate-in fade-in duration-500">
      
      {/* TOP NAVIGATION */}
      <button 
        onClick={() => navigate(-1)} 
        className="flex items-center gap-2 text-slate-500 hover:text-indigo-600 transition-colors font-bold text-sm"
      >
        <ArrowLeft size={18} /> Back to Member Registry
      </button>

      {/* HEADER SECTION */}
      <div className="bg-white rounded-[2.5rem] p-8 border border-slate-100 shadow-xl shadow-slate-200/50 flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
        <div className="flex items-center gap-6">
          <div className="h-20 w-20 bg-indigo-600 rounded-3xl flex items-center justify-center text-white text-3xl font-black shadow-lg shadow-indigo-200">
            {member.User?.full_name?.charAt(0)}
          </div>
          <div>
            <div className="flex items-center gap-3 mb-1">
              <h1 className="text-3xl font-black text-slate-900 tracking-tight">{member.User?.full_name}</h1>
              <span className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest border ${
                member.status === 'active' ? 'bg-emerald-50 text-emerald-700 border-emerald-100' : 'bg-slate-50 text-slate-400 border-slate-100'
              }`}>
                {member.status}
              </span>
            </div>
            <p className="text-slate-500 font-mono font-bold tracking-tighter uppercase">{member.member_number}</p>
          </div>
        </div>
        
        <div className="flex items-center gap-4">
           <div className={`p-4 rounded-2xl border ${isHighRisk ? 'bg-rose-50 border-rose-100 text-rose-700' : 'bg-slate-50 border-slate-100 text-slate-600'}`}>
              <p className="text-[10px] font-black uppercase tracking-widest opacity-60">Missed Payments</p>
              <p className="text-2xl font-black">{member.missed_pay_count}</p>
           </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        
        {/* CONTACT & SYSTEM INFO */}
        <div className="lg:col-span-1 space-y-6">
          <div className="bg-white p-6 rounded-[2rem] border border-slate-100 shadow-sm">
            <h3 className="text-xs font-black uppercase tracking-widest text-slate-400 mb-6 flex items-center gap-2">
              <User size={14} /> Contact Information
            </h3>
            <div className="space-y-4">
              <div className="flex items-center gap-4 text-slate-600">
                <div className="p-2 bg-slate-50 rounded-lg"><Mail size={16} /></div>
                <span className="text-sm font-bold truncate">{member.User?.email}</span>
              </div>
              <div className="flex items-center gap-4 text-slate-600">
                <div className="p-2 bg-slate-50 rounded-lg"><Phone size={16} /></div>
                <span className="text-sm font-bold">{member.User?.phone || 'No phone provided'}</span>
              </div>
              <div className="flex items-center gap-4 text-slate-600">
                <div className="p-2 bg-slate-50 rounded-lg"><MapPin size={16} /></div>
                <span className="text-sm font-bold uppercase tracking-tight">{member.AdminUnit?.name}</span>
              </div>
            </div>
          </div>

          <div className="bg-slate-900 p-6 rounded-[2rem] text-white shadow-xl">
             <h3 className="text-xs font-black uppercase tracking-widest text-slate-400 mb-6 flex items-center gap-2">
               <ShieldCheck size={14} /> System Registry
             </h3>
             <div className="space-y-3">
               <div>
                 <p className="text-[9px] font-bold text-slate-500 uppercase">Registered Date</p>
                 <p className="font-bold text-sm italic text-indigo-300">{formatDate(member.createdAt)}</p>
               </div>
               <div>
                 <p className="text-[9px] font-bold text-slate-500 uppercase">Member Type</p>
                 <p className="font-bold text-sm uppercase tracking-widest">Premium Allocation</p>
               </div>
             </div>
          </div>
        </div>

        {/* BENEFICIARIES SECTION */}
        <div className="lg:col-span-2">
          <div className="bg-white p-8 rounded-[2rem] border border-slate-100 shadow-sm h-full">
            <div className="flex items-center justify-between mb-8">
              <h3 className="text-xs font-black uppercase tracking-widest text-indigo-600 flex items-center gap-2">
                <Users size={16} /> Designated Beneficiaries
              </h3>
              <span className="text-[10px] font-black text-slate-400 uppercase tracking-tighter">Total: {member.Beneficiaries?.length || 0}</span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {member.Beneficiaries?.length > 0 ? (
                member.Beneficiaries.map((b) => (
                  <div key={b.id} className="p-4 bg-slate-50 rounded-2xl border border-slate-100 hover:border-indigo-200 transition-all group">
                    <div className="flex justify-between items-start mb-3">
                       <div className="h-10 w-10 bg-white rounded-xl flex items-center justify-center text-slate-400 group-hover:text-indigo-600 transition-colors">
                          <User size={20} />
                       </div>
                       <span className="bg-white px-2 py-1 rounded-lg text-[10px] font-black text-indigo-600 border border-indigo-100 shadow-sm">
                         {b.allocation}%
                       </span>
                    </div>
                    <p className="font-black text-slate-900 uppercase tracking-tight leading-none mb-1">{b.full_name}</p>
                    <p className="text-[11px] font-bold text-slate-400 italic">{b.relationship}</p>
                  </div>
                ))
              ) : (
                <div className="col-span-2 py-12 text-center border-2 border-dashed border-slate-100 rounded-[2rem]">
                  <p className="text-slate-400 text-sm font-medium italic">No beneficiaries registered for this member.</p>
                </div>
              )}
            </div>
            
            <div className="mt-8 p-4 bg-indigo-50/50 rounded-2xl border border-indigo-100 flex items-center gap-3">
              <CreditCard className="text-indigo-600" size={20} />
              <p className="text-[11px] font-bold text-indigo-700 leading-snug">
                Beneficiary allocations are used during welfare fund distribution calculations.
              </p>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default MemberDetail;